/**
 * Super Admin — Master Audit Profile Questions.
 *
 * Simplified per user spec (Feb 2026): the admin only writes the
 * question text. Every question on the customer side automatically
 * gets both a text answer and a file upload — no format picker here.
 *
 * Sticky header matches SuperAdminCustomers / SuperAdminTechnologies:
 * Admin Dashboard back button, gradient icon, title, Super Admin badge,
 * user dropdown with logout.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Archive, ArchiveRestore, Save, X, Pencil, ClipboardList,
  ArrowLeft, Crown, User, ChevronDown, LogOut, GripVertical, Download, Upload,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel,
  DropdownMenuItem, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { useAuth } from '../App';
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export default function SuperAdminAuditProfile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(true);
  const [editing, setEditing] = useState(null); // { id?, text }
  const [saving, setSaving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null); // row pending archive confirm
  const [importing, setImporting] = useState(false);
  const fileInputRef = React.useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/super-admin/master-audit-questions', {
        params: { include_archived: true },
      });
      setItems(data.items || []);
    } catch {
      toast.error('Failed to load master questions');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing?.text?.trim()) { toast.error('Question text is required'); return; }
    setSaving(true);
    try {
      const payload = { text: editing.text.trim(), answer_format: 'text' };
      if (editing.id) {
        await axios.put(`/api/super-admin/master-audit-questions/${editing.id}`, payload);
      } else {
        await axios.post('/api/super-admin/master-audit-questions', payload);
      }
      toast.success('Saved');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const setArchived = async (row, archived) => {
    try {
      await axios.post(
        `/api/super-admin/master-audit-questions/${row.id}/${archived ? 'archive' : 'unarchive'}`,
      );
      toast.success(archived ? 'Archived' : 'Unarchived');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
  };

  const visible = items.filter((it) => showArchived || !it.archived);

  const handleExport = async () => {
    try {
      const { data } = await axios.get('/api/super-admin/master-audit-questions/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url;
      a.download = `nova-master-audit-questions-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${data.count} question${data.count === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setImporting(true);
    try {
      const raw = await file.text();
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        toast.error('That file is not valid JSON.');
        return;
      }
      // Accept either the exported wrapper { questions: [...] } or a bare array.
      const questions = Array.isArray(parsed) ? parsed : (parsed.questions || []);
      if (!Array.isArray(questions) || questions.length === 0) {
        toast.error('No questions found in the file.');
        return;
      }
      const { data } = await axios.post('/api/super-admin/master-audit-questions/import', { questions });
      if (data.added > 0) {
        toast.success(`Imported ${data.added} question${data.added === 1 ? '' : 's'}${data.skipped ? ` · skipped ${data.skipped} duplicate/invalid` : ''}`);
      } else {
        toast.info(`Nothing added · ${data.skipped} skipped (already exist or invalid)`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // All non-archived / archived-toggled visible rows are sortable now —
  // including the Test Credentials system row. The row keeps its "System"
  // badge but supports the same edit / archive / reorder actions as any
  // other master question.
  const sortableRows = visible;
  const sortableIds = sortableRows.map((r) => r.id);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sortableIds.indexOf(active.id);
    const newIdx = sortableIds.indexOf(over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    // Optimistic reorder. `items` includes archived + system rows; we only
    // reshuffle the visible non-system set and keep everything else where
    // it was.
    const reordered = arrayMove(sortableRows, oldIdx, newIdx);
    const reorderedIds = reordered.map((r) => r.id);
    setItems((prev) => {
      const map = new Map(prev.map((r) => [r.id, r]));
      const out = [];
      let ptr = 0;
      for (const r of prev) {
        if ((showArchived || !r.archived) && sortableIds.includes(r.id)) {
          out.push(map.get(reorderedIds[ptr++]));
        } else {
          out.push(r);
        }
      }
      return out;
    });
    try {
      await axios.post('/api/super-admin/master-audit-questions/reorder', {
        ordered_ids: reorderedIds,
      });
      toast.success('Order saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reorder failed');
      load();
    }
  };

  return (
    <div className="min-h-screen bg-appbg" data-testid="super-audit-profile-page">
      {/* Sticky header — identical structure to SuperAdminCustomers. */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Button>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Audit Profile</h1>
                  <p className="text-sm text-slate-600">Master questions shown on every customer's Audit Profile</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100 hover:!text-purple-700">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                      <p className="text-xs text-slate-500">Super Administrator</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={logout}
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Page body — same max-w and horizontal padding as the header. */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-end gap-2 mb-4">
          <Button
            variant="outline"
            onClick={() => setShowArchived((v) => !v)}
            className="rounded-[10px] mr-auto"
            data-testid="master-toggle-archived"
          >
            {showArchived ? (
              <>
                <ArchiveRestore className="w-4 h-4 mr-1" /> Hide archived
              </>
            ) : (
              <>
                <Archive className="w-4 h-4 mr-1" /> Show archived
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            className="rounded-[10px]"
            data-testid="master-export-btn"
            title="Download all master questions as a JSON file"
          >
            <Download className="w-4 h-4 mr-1" /> Export
          </Button>
          <input
            type="file"
            accept="application/json,.json"
            ref={fileInputRef}
            onChange={handleImportFile}
            className="hidden"
            data-testid="master-import-input"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="rounded-[10px]"
            data-testid="master-import-btn"
            title="Import questions from a JSON file (adds new ones, skips duplicates)"
          >
            <Upload className="w-4 h-4 mr-1" /> {importing ? 'Importing…' : 'Import'}
          </Button>
          <Button
            onClick={() => setEditing({ text: '' })}
            className="bg-[#0F62FE] hover:bg-[#0A4FCC] text-white rounded-[10px]"
            data-testid="master-add-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add question
          </Button>
        </div>

        {editing && (
          <div
            className="mb-5 rounded-xl border border-[#0F62FE]/30 bg-[#F1F5FF] p-4"
            data-testid="master-edit-form"
          >
            <label className="text-xs font-medium text-[#0A0A0A] block mb-2">
              {editing.id ? 'Edit question' : 'New question'}
            </label>
            <div className="flex items-start gap-2">
              <Input
                autoFocus
                value={editing.text}
                onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                placeholder="e.g. Describe your organization's data retention policy…"
                maxLength={600}
                onKeyDown={(e) => e.key === 'Enter' && save()}
                data-testid="master-edit-text"
                className="flex-1"
              />
              <Button
                onClick={save}
                disabled={saving}
                className="bg-[#0F62FE] hover:bg-[#0A4FCC] text-white rounded-[10px]"
                data-testid="master-save-btn"
              >
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setEditing(null)}
                className="rounded-[10px]"
                data-testid="master-cancel-btn"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        <div
          className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden"
          data-testid="master-list"
        >
          {loading && (
            <div className="p-6 text-center text-sm text-[#94A3B8]">Loading…</div>
          )}
          {!loading && visible.length === 0 && (
            <div className="p-8 text-center text-sm text-[#64748B]">No master questions yet.</div>
          )}
          <ul className="divide-y divide-[#F1F5F9]">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                {sortableRows.map((it, idx) => (
                  <SortableRow
                    key={it.id}
                    it={it}
                    index={idx + 1}
                    onEdit={() => setEditing({ id: it.id, text: it.text })}
                    onArchiveRequest={() => setArchiveTarget(it)}
                    onUnarchive={() => setArchived(it, false)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </ul>
        </div>
      </div>

      {/* Archive confirmation — nothing is hard-deleted, but the question
          disappears for every customer until unarchived. */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent data-testid="master-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this question?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block mb-2">
                <b>“{archiveTarget?.text}”</b>
              </span>
              This will hide the question from every customer's Audit Profile
              immediately. It is not deleted — you can unarchive it any time to
              bring it back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="master-archive-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const row = archiveTarget;
                setArchiveTarget(null);
                if (row) await setArchived(row, true);
              }}
              className="bg-[#0F62FE] hover:bg-[#0A4FCC] text-white"
              data-testid="master-archive-confirm-btn"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


/* -------------------------------------------------------------------- */
function SortableRow({ it, index, onEdit, onArchiveRequest, onUnarchive }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: it.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
    boxShadow: isDragging ? '0 8px 24px rgba(15, 23, 42, 0.12)' : undefined,
    background: isDragging ? '#F1F5FF' : undefined,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`px-4 py-3 flex items-start gap-3 ${it.archived ? 'bg-[#FBFBFC]' : ''}`}
      data-testid={`master-row-${it.id}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="mt-0.5 shrink-0 text-[#94A3B8] hover:text-[#0F62FE] cursor-grab active:cursor-grabbing p-1 -m-1 rounded"
        title="Drag to reorder"
        data-testid={`master-drag-${it.id}`}
        aria-label="Drag to reorder"
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span className="text-[13px] font-semibold text-[#94A3B8] tabular-nums shrink-0 mt-0.5 w-6">
        {String(index).padStart(2, '0')}.
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] text-[#0A0A0A]">{it.text}</span>
          {it.system && (
            <Badge className="text-[10px] bg-[#EDE9FE] text-[#5B21B6] border border-[#DDD6FE]">
              System · encrypted table
            </Badge>
          )}
          {it.archived && (
            <Badge className="text-[10px] bg-[#FEE2E2] text-[#B91C1C] border border-[#FCA5A5]">
              Archived
            </Badge>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button
          size="sm"
          variant="ghost"
          onClick={onEdit}
          data-testid={`master-edit-${it.id}`}
          title="Edit"
        >
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        {it.archived ? (
          <Button
            size="sm"
            variant="outline"
            onClick={onUnarchive}
            className="rounded-[8px] h-8 px-2.5 text-[#047857] border-[#A7F3D0] hover:bg-[#ECFDF5]"
            data-testid={`master-unarchive-${it.id}`}
          >
            <ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Unarchive
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            onClick={onArchiveRequest}
            className="rounded-[8px] h-8 px-2.5 text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC]"
            data-testid={`master-archive-${it.id}`}
          >
            <Archive className="w-3.5 h-3.5 mr-1" /> Archive
          </Button>
        )}
      </div>
    </li>
  );
}
